<template>
  <div class="items suggestion-list">
    <div
      v-for="(item, index) in filteredUsers"
      :key="item.id"
      class="suggestion-list__item"
      :class="{ 'is-selected': index === selectedIndex }"
      @click="selectItem(index)"
    >
      <!-- User mention (simple, single line) -->
      <div v-if="isUser(item)" class="mention-item mention-item--user">
        <span class="mention-icon">👤</span>
        <span class="mention-name">{{ item.name }}</span>
      </div>

      <!-- Contact mention (multi-line with hierarchy) -->
      <div v-else class="mention-item mention-item--contact">
        <span class="mention-icon">{{ getContactIcon(item) }}</span>
        <div class="mention-content">
          <div class="mention-name">{{ item.name }}</div>
          <div
            v-if="getContactSubtitle(item, filteredUsers)"
            class="mention-subtitle"
          >
            {{ getContactSubtitle(item, filteredUsers) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    items: {
      type: Array,
      required: true,
    },

    command: {
      type: Function,
      required: true,
    },
    query: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      selectedIndex: 0,
    };
  },

  computed: {
    filteredUsers() {
      if (this.query == "") return this.items;
      const self = this;
      return this.items.filter(function (item) {
        return (
          item["name"].substring(0, self.query.length).toLowerCase() ==
          self.query.toLowerCase()
        );
      });
    },
  },

  watch: {
    items() {
      this.selectedIndex = 0;
    },
  },

  methods: {
    onKeyDown({ event }) {
      if (event.key === "ArrowUp") {
        this.upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        this.downHandler();
        return true;
      }

      if (event.key === "Enter") {
        this.enterHandler();
        return true;
      }

      return false;
    },

    upHandler() {
      this.selectedIndex =
        (this.selectedIndex + this.items.length - 1) % this.items.length;
    },

    downHandler() {
      this.selectedIndex = (this.selectedIndex + 1) % this.items.length;
    },

    enterHandler() {
      this.selectItem(this.selectedIndex);
    },

    selectItem(index) {
      const item = this.filteredUsers[index];

      if (item) {
        this.command({ id: item.id, label: item.name });
      }
    },

    // Check if item is a user (has no type) or contact (has type field)
    isUser(item) {
      // User items have id "*" or don't have a type field
      // Contacts have a type field like "organization", "department", "individual"
      return item.id === "*" || !item.type;
    },

    // Get icon based on contact type
    getContactIcon(item) {
      if (!item.type) return "👤";

      const type = String(item.type).toLowerCase();

      if (type.includes("organization")) return "🏢";
      if (type.includes("department")) return "🏛️";
      if (type.includes("individual")) return "👤";

      return "👤";
    },

    // Get subtitle (hierarchy info) for contacts
    getContactSubtitle(item, allItems) {
      if (!item.type) return null;

      const type = String(item.type).toLowerCase();

      // For Individual: show full hierarchy "Company > Department" or just "Department/Company"
      if (type.includes("individual")) {
        if (!item.parent_name) return null;

        // Find the parent contact to check if it's a department
        const parent = allItems.find((c) => c.name === item.parent_name);

        if (
          parent &&
          parent.type &&
          parent.type.toLowerCase().includes("department")
        ) {
          // Parent is a department, check if department has a parent (company)
          if (parent.parent_name) {
            return `${parent.parent_name} > ${item.parent_name}`;
          }
        }

        // Parent is a company or parent not found - just show parent name
        return item.parent_name;
      }

      // For Department: show "Company"
      if (type.includes("department")) {
        return item.parent_name || null;
      }

      // For Organization: no subtitle
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
$color-black: #000000;
$color-white: #ffffff;
// .items {
//   position: relative;
//   border-radius: 0.25rem;
//   background: white;
//   color: rgba(black, 0.8);
//   overflow: hidden;
//   font-size: 0.9rem;
//   box-shadow:
//     0 0 0 1px rgba(0, 0, 0, 0.1),
//     0px 10px 20px rgba(0, 0, 0, 0.1),
//   ;
// }

// .item {
//   display: block;
//   width: 100%;
//   text-align: left;
//   background: transparent;
//   border: none;
//   padding: 0.2rem 0.5rem;

//   &.is-selected,
//   &:hover {
//     color: #A975FF;
//     background: rgba(#A975FF, 0.1);
//   }
// }

.suggestion-list {
  font-family: Roboto;
  padding: 0.4rem;
  border: 2px solid rgba($color-black, 0.1);
  border-radius: 8px;
  background: white;
  box-shadow: 0 4px 12px rgba($color-black, 0.15);
  min-width: 250px;
  max-width: 400px;

  &__no-results {
    padding: 0.4rem 0.6rem;
    color: rgba($color-black, 0.5);
    font-size: 0.85rem;
  }

  &__item {
    border-radius: 6px;
    padding: 0.5rem 0.6rem;
    margin-bottom: 0.3rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &.is-selected,
    &:hover {
      background-color: rgba(#1976d2, 0.08);
      transform: translateX(2px);
    }

    &.is-empty {
      opacity: 0.5;
    }
  }
}

.mention-item {
  display: flex;
  align-items: center;
  gap: 10px;

  &--user {
    // Simple single-line layout for users
    font-size: 0.9rem;
    font-weight: 500;
  }

  &--contact {
    // Multi-line layout for contacts
    align-items: flex-start;
  }
}

.mention-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  line-height: 1;
}

.mention-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mention-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba($color-black, 0.9);
  line-height: 1.3;
  word-break: break-word;
}

.mention-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba($color-black, 0.6);
  line-height: 1.2;
  word-break: break-word;
}
</style>
